import React, { useEffect, useMemo, useState } from 'react';
import { ArrowCounterClockwise, Archive, Check, CircleNotch, EnvelopeSimple, Sparkle, WarningCircle, X } from '@phosphor-icons/react';
import { ImmoButton, ObjektPicker, TextFeld, type ObjektPickerOption } from '../../../../components/immobilien/ui';
import { formatiereGroesse } from '../../../../lib/dokumente/aktenschrankApi';
import { flacheOrdner } from '../../../../lib/dokumente/aktenschrank';
import { ablageHinweis, absenderTypLabel, herkunftLabel, segmentVonStatus, sicherheitTon } from '../../../../lib/dokumente/ablage';
import { mitObjektnummer } from '../../../../lib/dokumente/dateiname';
import type { AblageZeile } from '../../../../lib/dokumente/useZurAblage';
import type { AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import { deDatumZeit, dateiDarstellung } from '../aktenschrank/dateiDarstellung';
import type { OrdnerQuelle } from './ordnerQuelle';
import AnhangVorschau from '../../emails/AnhangVorschau';
import { OrdnerAuswahl } from './OrdnerAuswahl';
import { vorschauTyp } from '../../../../lib/emails/anhangDatei';

const TON = { ok: 'var(--ok)', warn: 'var(--warn)', bad: 'var(--bad)' } as const;
const STATUS_PILL: Record<string, { text: string; fg: string; bg: string }> = {
  offen: { text: 'Offen', fg: 'var(--warn)', bg: 'var(--warn-soft)' },
  abgelegt: { text: 'Abgelegt', fg: 'var(--ok)', bg: 'var(--ok-soft)' },
  abgelehnt: { text: 'Abgelehnt', fg: 'var(--ink2)', bg: 'var(--fill2)' },
};

export interface KartenAktionen {
  onAblegen: (z: AblageZeile, werte: { objektId: string; zielPfad: string; dateiname: string }) => Promise<void>;
  onAblehnen: (z: AblageZeile) => Promise<void>;
  onZurueck: (z: AblageZeile) => Promise<void>;
  onOeffnen: (z: AblageZeile) => void;
}

/** Vorschlagskarte (Handoff Dokumente §6.3, Screens 07/08): Anhang, Absender, KI, Sicherheit; vor dem Ablegen bearbeitbar. */
export function VorschlagKarte({ z, objekte, pickerOptionen, ordner, aktionen, gesperrt }: {
  z: AblageZeile;
  objekte: readonly AktObjekt[];
  pickerOptionen: readonly ObjektPickerOption[];
  ordner: OrdnerQuelle;
  aktionen: KartenAktionen;
  /** Während der Massenablage keine Einzelaktionen. */
  gesperrt: boolean;
}) {
  const segment = segmentVonStatus(z.status);
  const name = z.anhang?.dateiname || z.dateiname;
  const d = dateiDarstellung(name, z.anhang?.dateityp);
  const pill = STATUS_PILL[segment];
  const prozent = Math.round(z.confidence * 100);

  const [objektId, setObjektId] = useState<string | null>(z.objekt_id);
  const [zielPfad, setZielPfad] = useState(z.ziel_pfad ?? '');
  const [dateiname, setDateiname] = useState(z.dateiname);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(z.status === 'fehler' ? z.fehler : null);

  const ordnerListe = ordner.liste(objektId);
  useEffect(() => { if (objektId) ordner.lade(objektId); }, [objektId, ordner]);
  const optionen = useMemo(() => {
    const flach = ordnerListe ? flacheOrdner(ordnerListe) : [];
    if (!zielPfad || flach.some(o => o.pfad === zielPfad)) return flach;
    // Ein Zielpfad aus der Regel ({Jahr}) existiert evtl. noch nicht – das Backend legt ihn beim Ablegen an.
    return [{ pfad: zielPfad, name: ordnerListe ? `${zielPfad} (wird angelegt)` : zielPfad, tiefe: 0 }, ...flach];
  }, [ordnerListe, zielPfad]);

  const wechsleObjekt = (id: string) => {
    if (id === objektId) return;
    setObjektId(id);
    const nr = objekte.find(o => o.id === id)?.nummer;
    setDateiname(n => mitObjektnummer(n, nr));
  };

  const hinweis = ablageHinweis({
    objekt_id: objektId, ziel_pfad: zielPfad, dateiname, graph_message_id: z.anhang?.graph_message_id ?? null, graph_attachment_id: z.anhang?.graph_attachment_id ?? null,
  });
  const fuehreAus = async (f: () => Promise<void>) => {
    setLaeuft(true); setFehler(null);
    try { await f(); } catch (e) { setFehler(e instanceof Error ? e.message : 'Aktion fehlgeschlagen'); } finally { setLaeuft(false); }
  };

  const absender = z.email?.von_name || z.email?.von || 'Unbekannter Absender';
  const typ = absenderTypLabel(z.email?.analyse?.absender_typ);
  const sperre = laeuft || gesperrt;

  return (
    <article className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 16, gap: 12 }} aria-label={name}>
      <div className="flex items-start" style={{ gap: 12 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 14, background: d.bg }}><d.icon weight="fill" size={22} color={d.fg} /></span>
        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
          <span style={{ fontSize: 16, fontWeight: 800, overflowWrap: 'anywhere' }}>{name}</span>
          <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
            {[formatiereGroesse(z.anhang?.dateigroesse != null ? String(z.anhang.dateigroesse) : null), z.email?.datum ? `Anhang vom ${deDatumZeit(z.email.datum)}` : null].filter(Boolean).join(' · ')}
          </span>
        </div>
        <span className="shrink-0" style={{ height: 26, padding: '0 10px', borderRadius: 13, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 800, color: pill.fg, background: pill.bg }}>
          {z.status === 'fehler' ? 'Fehler' : pill.text}
        </span>
      </div>

      <div className="immo-innen flex flex-col min-w-0" style={{ borderRadius: 16, padding: '10px 14px', gap: 4 }}>
        <span className="flex items-center flex-wrap" style={{ gap: 7, fontSize: 13, fontWeight: 800 }}>
          <EnvelopeSimple weight="fill" size={14} color="var(--acc)" />
          <span className="truncate" style={{ maxWidth: '100%' }}>{absender}</span>
          {typ && <span style={{ height: 20, padding: '0 8px', borderRadius: 10, display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)' }}>{typ}</span>}
        </span>
        {z.email?.betreff && <span style={{ fontSize: 13, overflowWrap: 'anywhere' }}>{z.email.betreff}</span>}
      </div>

      {/* #519: Dokument zur Kontrolle ansehen (PDF/Bild, lädt erst auf Klick) bzw. herunterladen */}
      {z.anhang && (
        <AnhangVorschau anhangId={z.anhang.id} dateiname={name} dateityp={vorschauTyp(z.anhang.dateityp, z.anhang.dateiname)}
          hatGraphRef={!!(z.anhang.graph_message_id && z.anhang.graph_attachment_id)} />
      )}

      {z.email?.analyse?.zusammenfassung && (
        <span className="flex items-start" style={{ gap: 8, fontSize: 13, color: 'var(--ink2)', lineHeight: 1.45 }}>
          <Sparkle weight="fill" size={14} color="var(--acc)" style={{ flexShrink: 0, marginTop: 2 }} />{z.email.analyse.zusammenfassung}
        </span>
      )}

      <div className="flex items-center flex-wrap" style={{ gap: 10, fontSize: 11 }}>
        <span style={{ fontWeight: 700, color: 'var(--ink3)' }}>Sicherheit</span>
        <span style={{ flex: '1 1 120px', height: 6, borderRadius: 3, background: 'var(--fill3)', overflow: 'hidden' }} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={prozent} aria-label="Sicherheit">
          <span style={{ display: 'block', height: '100%', width: `${prozent}%`, background: TON[sicherheitTon(z.confidence)] }} />
        </span>
        <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 800, color: TON[sicherheitTon(z.confidence)] }}>{prozent} %</span>
        <span className="truncate" style={{ color: 'var(--ink3)', maxWidth: '100%' }}>{herkunftLabel(z.regel?.name, z.confidence)}</span>
      </div>

      {segment === 'offen' && (
        <div className="immo-innen flex flex-col min-w-0" style={{ borderRadius: 20, padding: 14, gap: 12 }}>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Zielobjekt</span>
            <div><ObjektPicker optionen={pickerOptionen} wert={objektId} onWahl={wechsleObjekt} /></div>
          </div>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Zielordner</span>
            {/* #518: eigene Auswahl mit Suche und farbig abgesetzten Hauptordnern */}
            <OrdnerAuswahl optionen={optionen} wert={zielPfad} onWahl={setZielPfad} disabled={!objektId || sperre}
              platzhalter={objektId ? (ordnerListe ? 'Zielordner wählen …' : 'Ordner werden geladen …') : 'Zuerst ein Objekt wählen'} />
          </div>
          <TextFeld label="Dateiname" wert={dateiname} onChange={setDateiname} kompakt disabled={sperre}
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, fontWeight: 700, color: 'var(--ink)', fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" }} />
          {(fehler || hinweis) && (
            <span className="flex items-start" style={{ gap: 6, fontSize: 12, color: fehler ? 'var(--bad)' : 'var(--warn)' }}>
              <WarningCircle weight="fill" size={14} style={{ flexShrink: 0, marginTop: 1 }} />{fehler ?? hinweis}
            </span>
          )}
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton art="fuellung" groesse={44} icon={X} disabled={sperre} onClick={() => void fuehreAus(() => aktionen.onAblehnen(z))}>Ablehnen</ImmoButton>
            <ImmoButton art="akzent" groesse={44} icon={laeuft ? CircleNotch : Check} disabled={sperre || !!hinweis || !objektId}
              onClick={() => objektId && void fuehreAus(() => aktionen.onAblegen(z, { objektId, zielPfad, dateiname }))}>
              {laeuft ? 'Wird abgelegt …' : 'Ablegen'}
            </ImmoButton>
          </div>
        </div>
      )}

      {segment === 'abgelegt' && (
        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
          <span className="flex-1 min-w-0" style={{ fontSize: 12, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>
            {[z.ziel_pfad?.split('/').join(' › '), z.entschieden_at ? `abgelegt am ${deDatumZeit(z.entschieden_at)}` : null].filter(Boolean).join(' · ')}
          </span>
          {z.gdrive_file_id && <ImmoButton groesse={44} icon={Archive} onClick={() => aktionen.onOeffnen(z)}>Im Aktenschrank öffnen</ImmoButton>}
        </div>
      )}

      {segment === 'abgelehnt' && (
        <div className="flex items-center justify-end flex-wrap" style={{ gap: 10 }}>
          {fehler && <span className="flex-1" style={{ fontSize: 12, color: 'var(--bad)' }}>{fehler}</span>}
          <ImmoButton groesse={44} icon={ArrowCounterClockwise} disabled={sperre} onClick={() => void fuehreAus(() => aktionen.onZurueck(z))}>Zurück in die Warteschlange</ImmoButton>
        </div>
      )}
    </article>
  );
}
